import { useState, useRef, useCallback, useEffect } from "react";
import {
  Plus,
  X,
  ShoppingBag,
  Truck,
  Shield,
  RotateCcw,
  Lock,
  ChevronDown,
  ArrowLeft,
  ImageIcon,
} from "lucide-react";

/* ────────────────────────────────────────────────
   Store-Specific Section Block Types
   5 composite blocks for e-commerce Page Builder
   ──────────────────────────────────────────────── */

import type { ContentBlock } from "./contentBlockTypes";
import { useSectionDropSlot } from "./SectionDropSlotContext";
import { EditableImage } from "./MediaPickerModal";
import { handleFormatKeyDown, extractRichValue, hasHtmlFormatting } from "./InlineFormatToolbar";
import { BlockFormatBar } from "./BlockFormatBar";
import type { TypoData } from "./BlockFormatBar";

/* ═══════ Type Guard ═══════ */

const STORE_SECTION_TYPES = new Set([
  "sectionFeaturedProducts",
  "sectionProductCategories",
  "sectionPromoBanner",
  "sectionStoreBenefits",
  "sectionFAQ",
]);

export function isStoreSectionType(type: string): boolean {
  return STORE_SECTION_TYPES.has(type);
}

/* ═══════ Default Content ═══════ */

export function getStoreDefaultContent(type: string): string | undefined {
  switch (type) {
    case "sectionFeaturedProducts":
      return "\u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05D4\u05DE\u05D5\u05D1\u05D9\u05DC\u05D9\u05DD \u05E9\u05DC\u05E0\u05D5";
    case "sectionProductCategories":
      return "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD";
    case "sectionPromoBanner":
      return "\u05DE\u05D1\u05E6\u05E2 \u05DE\u05D9\u05D5\u05D7\u05D3!";
    case "sectionStoreBenefits":
      return "\u05DC\u05DE\u05D4 \u05DC\u05E7\u05E0\u05D5\u05EA \u05D0\u05E6\u05DC\u05E0\u05D5?";
    case "sectionFAQ":
      return "\u05E9\u05D0\u05DC\u05D5\u05EA \u05E0\u05E4\u05D5\u05E6\u05D5\u05EA";
    default:
      return undefined;
  }
}

/* ═══════ Default Meta ═══════ */

export function getStoreDefaultMeta(type: string): Record<string, any> | undefined {
  switch (type) {
    case "sectionFeaturedProducts":
      return {
        subtitle: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E0\u05D1\u05D7\u05E8\u05D9\u05DD \u05E9\u05D4\u05DC\u05E7\u05D5\u05D7\u05D5\u05EA \u05E9\u05DC\u05E0\u05D5 \u05D0\u05D5\u05D4\u05D1\u05D9\u05DD",
        items: [
          { name: "\u05DE\u05D5\u05E6\u05E8 \u05E8\u05D0\u05E9\u05D5\u05DF", price: "\u20AA199", badge: "" },
          { name: "\u05DE\u05D5\u05E6\u05E8 \u05E9\u05E0\u05D9", price: "\u20AA249", badge: "\u05D7\u05D3\u05E9" },
          { name: "\u05DE\u05D5\u05E6\u05E8 \u05E9\u05DC\u05D9\u05E9\u05D9", price: "\u20AA179", badge: "\u05DE\u05D1\u05E6\u05E2" },
          { name: "\u05DE\u05D5\u05E6\u05E8 \u05E8\u05D1\u05D9\u05E2\u05D9", price: "\u20AA329", badge: "" },
        ],
        buttonLabel: "\u05DC\u05DB\u05DC \u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
        buttonLink: "/catalog",
      };
    case "sectionProductCategories":
      return {
        subtitle: "\u05D2\u05DC\u05E9\u05D5 \u05D1\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E9\u05DC\u05E0\u05D5",
        items: [
          { name: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05E8\u05D0\u05E9\u05D5\u05E0\u05D4", count: 12 },
          { name: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05E9\u05E0\u05D9\u05D9\u05D4", count: 8 },
          { name: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05E9\u05DC\u05D9\u05E9\u05D9\u05EA", count: 15 },
          { name: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05E8\u05D1\u05D9\u05E2\u05D9\u05EA", count: 6 },
        ],
      };
    case "sectionPromoBanner":
      return {
        subtitle: "\u05D4\u05E0\u05D7\u05D5\u05EA \u05DE\u05D8\u05D5\u05E8\u05E4\u05D5\u05EA \u05E2\u05DC \u05DE\u05D2\u05D5\u05D5\u05DF \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E0\u05D1\u05D7\u05E8\u05D9\u05DD",
        discount: "20% \u05D4\u05E0\u05D7\u05D4",
        buttonLabel: "\u05DC\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05D1\u05DE\u05D1\u05E6\u05E2",
        buttonLink: "/catalog",
        bgColor: "gradient-blue",
      };
    case "sectionStoreBenefits":
      return {
        items: [
          { icon: "truck", title: "\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD", description: "\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD \u05D1\u05D4\u05D6\u05DE\u05E0\u05D5\u05EA \u05DE\u05E2\u05DC \u20AA299" },
          { icon: "shield", title: "\u05D0\u05D7\u05E8\u05D9\u05D5\u05EA \u05DE\u05DC\u05D0\u05D4", description: "\u05D0\u05D7\u05E8\u05D9\u05D5\u05EA \u05D9\u05E6\u05E8\u05DF \u05E2\u05DC \u05DB\u05DC \u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD" },
          { icon: "return", title: "\u05D4\u05D7\u05D6\u05E8\u05D5\u05EA \u05E2\u05D3 30 \u05D9\u05D5\u05DD", description: "\u05DC\u05D0 \u05DE\u05E8\u05D5\u05E6\u05D9\u05DD? \u05D4\u05D7\u05D6\u05D9\u05E8\u05D5 \u05D1\u05E7\u05DC\u05D5\u05EA" },
          { icon: "lock", title: "\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7", description: "\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7 \u05D1-SSL" },
        ],
      };
    case "sectionFAQ":
      return {
        subtitle: "\u05EA\u05E9\u05D5\u05D1\u05D5\u05EA \u05DC\u05E9\u05D0\u05DC\u05D5\u05EA \u05D4\u05E0\u05E4\u05D5\u05E6\u05D5\u05EA \u05D1\u05D9\u05D5\u05EA\u05E8",
        items: [
          { question: "\u05DB\u05DE\u05D4 \u05D6\u05DE\u05DF \u05DC\u05D5\u05E7\u05D7 \u05DE\u05E9\u05DC\u05D5\u05D7?", answer: "\u05DE\u05E9\u05DC\u05D5\u05D7\u05D9\u05DD \u05DE\u05D2\u05D9\u05E2\u05D9\u05DD \u05EA\u05D5\u05DA 3\u20135 \u05D9\u05DE\u05D9 \u05E2\u05E1\u05E7\u05D9\u05DD. \u05DE\u05E9\u05DC\u05D5\u05D7 \u05D0\u05E7\u05E1\u05E4\u05E8\u05E1 \u05D6\u05DE\u05D9\u05DF \u05EA\u05D5\u05DA \u05D9\u05D5\u05DD \u05E2\u05E1\u05E7\u05D9\u05DD \u05D0\u05D7\u05D3." },
          { question: "\u05D0\u05D9\u05DA \u05D0\u05E4\u05E9\u05E8 \u05DC\u05D4\u05D7\u05D6\u05D9\u05E8 \u05DE\u05D5\u05E6\u05E8?", answer: "\u05E0\u05D9\u05EA\u05DF \u05DC\u05D4\u05D7\u05D6\u05D9\u05E8 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05EA\u05D5\u05DA 30 \u05D9\u05D5\u05DD \u05DE\u05D9\u05D5\u05DD \u05D4\u05E7\u05E0\u05D9\u05D9\u05D4, \u05D1\u05EA\u05E0\u05D0\u05D9 \u05E9\u05D4\u05DE\u05D5\u05E6\u05E8 \u05D1\u05DE\u05E6\u05D1 \u05EA\u05E7\u05D9\u05DF \u05D5\u05D1\u05D0\u05E8\u05D9\u05D6\u05D4 \u05D4\u05DE\u05E7\u05D5\u05E8\u05D9\u05EA." },
          { question: "\u05D0\u05D9\u05DC\u05D5 \u05D0\u05DE\u05E6\u05E2\u05D9 \u05EA\u05E9\u05DC\u05D5\u05DD \u05D6\u05DE\u05D9\u05E0\u05D9\u05DD?", answer: "\u05D0\u05E0\u05D5 \u05DE\u05E7\u05D1\u05DC\u05D9\u05DD \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05D0\u05E9\u05E8\u05D0\u05D9, PayPal, \u05D5\u05D4\u05E2\u05D1\u05E8\u05D5\u05EA \u05D1\u05E0\u05E7\u05D0\u05D9\u05D5\u05EA. \u05DB\u05DC \u05D4\u05EA\u05E9\u05DC\u05D5\u05DE\u05D9\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7\u05D9\u05DD \u05D1-SSL." },
          { question: "\u05D4\u05D0\u05DD \u05D9\u05E9 \u05DC\u05DB\u05DD \u05D7\u05E0\u05D5\u05EA \u05E4\u05D9\u05D6\u05D9\u05EA?", answer: "\u05DB\u05DF! \u05D0\u05E0\u05D5 \u05DE\u05E4\u05E2\u05D9\u05DC\u05D9\u05DD \u05D7\u05E0\u05D5\u05EA \u05E9\u05D5\u05E8\u05D5\u05DD \u05D1\u05D9\u05DE\u05D9\u05DD \u05D0\u05F3\u2013\u05D4\u05F3 \u05D1\u05D9\u05DF \u05D4\u05E9\u05E2\u05D5\u05EA 9:00\u201318:00. \u05DE\u05D5\u05D6\u05DE\u05E0\u05D9\u05DD \u05DC\u05D1\u05E7\u05E8!" },
        ],
      };
    default:
      return undefined;
  }
}

/* ═══════ Shared Helper Components ═══════ */

const TAG_BADGE_COLORS: Record<string, string> = {
  h2: "bg-blue-100 text-blue-700",
  h3: "bg-cyan-100 text-cyan-700",
  p: "bg-gray-100 text-gray-600",
  span: "bg-gray-50 text-gray-500",
  div: "bg-gray-100 text-gray-500",
};

const BLOCK_LEVEL = new Set(["h2", "h3", "p", "div"]);

function InlineEditable({
  value,
  onSave,
  isEditMode,
  tag: Tag = "p",
  className = "",
  placeholder = "\u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4...",
  inputClassName = "",
  typo,
  onTypoChange,
  onTypoReset,
}: {
  value: string;
  onSave: (val: string) => void;
  isEditMode: boolean;
  tag?: "h2" | "h3" | "p" | "span" | "div";
  className?: string;
  placeholder?: string;
  inputClassName?: string;
  typo?: TypoData;
  onTypoChange?: (t: TypoData) => void;
  onTypoReset?: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isRich = hasHtmlFormatting(value);

  const typoStyle: React.CSSProperties = {
    ...(typo?.fontSize ? { fontSize: typo.fontSize } : {}),
    ...(typo?.color ? { color: typo.color } : {}),
    ...(typo?.fontWeight ? { fontWeight: Number(typo.fontWeight) } : {}),
  };

  useEffect(() => {
    if (!isFocused && ref.current) {
      if (isRich) {
        ref.current.innerHTML = value;
      } else {
        ref.current.textContent = value;
      }
    }
  }, [value, isFocused, isRich]);

  const handleBlur = useCallback(() => {
    if (!ref.current) { setIsFocused(false); return; }
    const newVal = extractRichValue(ref.current);
    setIsFocused(false);
    if (newVal !== value) onSave(newVal);
  }, [value, onSave]);

  const renderContent = (text: string) => {
    if (hasHtmlFormatting(text)) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return text;
  };

  if (!isEditMode) {
    return <Tag className={className} style={typoStyle}>{value ? renderContent(value) : <span className="text-gray-300">{placeholder}</span>}</Tag>;
  }

  const isBlockTag = BLOCK_LEVEL.has(Tag);
  const Wrapper = isBlockTag ? "div" : "span";

  return (
    <Wrapper className={`relative ${isBlockTag ? "block" : "inline-block"} group/editable-store pt-5`}>
      <div className="absolute top-0 right-0 z-[20] flex items-center gap-2 opacity-80 group-hover/editable-store:opacity-100 transition-opacity">
        <span
          className={`inline-flex items-center text-xs leading-none px-2.5 py-1 rounded-full font-mono shadow-sm ${TAG_BADGE_COLORS[Tag] || "bg-gray-100 text-gray-500"}`}
          style={{ fontWeight: 700 }}
        >
          {Tag.toUpperCase()}
        </span>
        <BlockFormatBar
          editableEl={ref.current}
          typo={typo}
          onTypoChange={onTypoChange}
          onTypoReset={onTypoReset}
          showBullets
        />
      </div>
      <Tag
        ref={ref as any}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        } ${inputClassName}`}
        style={typoStyle}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (handleFormatKeyDown(e)) return;
          if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLElement).blur(); }
          if (e.key === "Escape") { if (ref.current) ref.current.textContent = value; (e.target as HTMLElement).blur(); }
        }}
        dir="rtl"
      />
    </Wrapper>
  );
}

function MultilineEditable({
  value,
  onSave,
  isEditMode,
  className = "",
  placeholder = "\u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4...",
  typo,
  onTypoChange,
  onTypoReset,
}: {
  value: string;
  onSave: (val: string) => void;
  isEditMode: boolean;
  className?: string;
  placeholder?: string;
  typo?: TypoData;
  onTypoChange?: (t: TypoData) => void;
  onTypoReset?: () => void;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isRich = hasHtmlFormatting(value);

  const typoStyle: React.CSSProperties = {
    ...(typo?.fontSize ? { fontSize: typo.fontSize } : {}),
    ...(typo?.color ? { color: typo.color } : {}),
    ...(typo?.fontWeight ? { fontWeight: Number(typo.fontWeight) } : {}),
  };

  useEffect(() => {
    if (!isFocused && ref.current) {
      if (isRich) {
        ref.current.innerHTML = value;
      } else {
        ref.current.innerHTML = value.replace(/\n/g, "<br>");
      }
    }
  }, [value, isFocused, isRich]);

  const handleBlur = useCallback(() => {
    if (!ref.current) { setIsFocused(false); return; }
    const newVal = extractRichValue(ref.current);
    setIsFocused(false);
    if (newVal !== value) onSave(newVal);
  }, [value, onSave]);

  const renderContent = (text: string) => {
    if (hasHtmlFormatting(text)) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return text.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ));
  };

  if (!isEditMode) {
    if (!value) return null;
    return <p className={className} style={typoStyle}>{renderContent(value)}</p>;
  }

  return (
    <div className="relative block group/editable-store pt-5">
      <div className="absolute top-0 right-0 z-[20] flex items-center gap-2 opacity-80 group-hover/editable-store:opacity-100 transition-opacity">
        <span
          className="inline-flex items-center text-xs leading-none px-2.5 py-1 rounded-full font-mono shadow-sm bg-gray-100 text-gray-600"
          style={{ fontWeight: 700 }}
        >
          P
        </span>
        <BlockFormatBar
          editableEl={ref.current}
          typo={typo}
          onTypoChange={onTypoChange}
          onTypoReset={onTypoReset}
          showBullets
        />
      </div>
      <p
        ref={ref}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        }`}
        style={typoStyle}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (handleFormatKeyDown(e)) return;
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const range = sel.getRangeAt(0);
              range.deleteContents();
              const br = document.createElement("br");
              range.insertNode(br);
              const txt = document.createTextNode("\u200B");
              range.setStartAfter(br);
              range.insertNode(txt);
              range.setStartAfter(txt);
              range.setEndAfter(txt);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
          if (e.key === "Escape") {
            if (ref.current) ref.current.innerHTML = value.replace(/\n/g, "<br>");
            ref.current?.blur();
          }
        }}
        dir="rtl"
      />
    </div>
  );
}

/* ────── Typography helper for store section fields ────── */

function makeFieldTypo(
  meta: Record<string, any> | undefined,
  onUpdateMeta: (m: Record<string, any>) => void,
  fieldKey: string,
): { typo: TypoData; onTypoChange: (t: TypoData) => void; onTypoReset: () => void } {
  const mk = `_typo_${fieldKey}`;
  const raw = meta?.[mk] as TypoData | undefined;
  return {
    typo: raw || {},
    onTypoChange: (t: TypoData) => onUpdateMeta({ [mk]: { ...(raw || {}), ...t } }),
    onTypoReset: () => onUpdateMeta({ [mk]: {} }),
  };
}

/* ═══════════════════════════════════════════════
   1. FEATURED PRODUCTS SECTION
   ═══════════════════════════════════════════════ */

interface FeaturedProductItem {
  name: string;
  price: string;
  badge: string;
  imageUrl?: string;
}

function SectionFeaturedProductsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: FeaturedProductItem[] = meta.items || [];
  const buttonLabel = meta.buttonLabel || "";
  const buttonLink = meta.buttonLink || "/catalog";

  const updateItem = (idx: number, field: string, val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { name: "\u05DE\u05D5\u05E6\u05E8 \u05D7\u05D3\u05E9", price: "\u20AA0", badge: "" }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-2"
          style={{ fontWeight: 700 }}
          placeholder="כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm text-center mb-10"
          placeholder="תיאור..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />

        {dropSlot(`secblk-${block.id}-after-subtitle`)}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, i) => (
            <div key={i} className="relative group/prod bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              {/* Product image */}
              <div className="aspect-[4/3] relative">
                <EditableImage
                  src={item.imageUrl}
                  onChangeSrc={(url) => updateItem(i, "imageUrl", url)}
                  isEditMode={isEditMode}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  placeholderClassName="aspect-[4/3]"
                />
                {/* Badge */}
                {(item.badge || isEditMode) && (
                  <div className="absolute top-2 right-2 z-10">
                    {isEditMode ? (
                      <input
                        type="text"
                        value={item.badge}
                        onChange={(e) => updateItem(i, "badge", e.target.value)}
                        className="px-2 py-0.5 text-[11px] rounded-full bg-red-500 text-white outline-none w-16 text-center placeholder-white/60"
                        dir="rtl"
                        placeholder="תג"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : item.badge ? (
                      <span className="px-2 py-0.5 text-[11px] rounded-full bg-red-500 text-white" style={{ fontWeight: 600 }}>
                        {item.badge}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
              {/* Content */}
              <div className="p-4">
                <InlineEditable
                  value={item.name}
                  onSave={(val) => updateItem(i, "name", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-zap-navy text-sm mb-1"
                  style={{ fontWeight: 700 }}
                  placeholder="שם המוצר..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_name`)}
                />
                {isEditMode ? (
                  <input
                    type="text"
                    value={item.price}
                    onChange={(e) => updateItem(i, "price", e.target.value)}
                    className="text-zap-navy text-sm outline-none border-b border-gray-200 focus:border-zap w-full bg-transparent"
                    style={{ fontWeight: 600 }}
                    dir="rtl"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <p className="text-zap-navy text-sm" style={{ fontWeight: 600 }}>{item.price}</p>
                )}
              </div>
              {/* Delete */}
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/prod:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {/* Add card */}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-zap-pale rounded-xl flex flex-col items-center justify-center text-zap-light hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[200px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>{"הוספת מוצר"}</span>
            </button>
          )}
        </div>

        {dropSlot(`secblk-${block.id}-after-content`)}

        {/* CTA Button */}
        {(buttonLabel || isEditMode) && (
          <div className="mt-8 flex justify-center">
            {isEditMode ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={buttonLabel}
                  onChange={(e) => onUpdateMeta({ buttonLabel: e.target.value })}
                  className="px-5 py-2.5 bg-zap text-white rounded-lg text-sm outline-none min-w-[100px] text-center"
                  style={{ fontWeight: 600 }}
                  dir="rtl"
                  placeholder="טקסט כפתור..."
                />
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-400">{"קישור:"}</span>
                  <input
                    type="text"
                    value={buttonLink}
                    onChange={(e) => onUpdateMeta({ buttonLink: e.target.value })}
                    className="text-[11px] px-2 py-1 border border-gray-200 rounded w-24"
                    dir="ltr"
                  />
                </div>
              </div>
            ) : buttonLabel ? (
              <span
                role="button"
                tabIndex={0}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-zap text-white rounded-lg text-sm cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {buttonLabel}
                <ArrowLeft className="w-4 h-4" />
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   2. PRODUCT CATEGORIES SECTION
   ═══════════════════════════════════════════════ */

interface CategoryItem {
  name: string;
  count: number;
  imageUrl?: string;
}

function SectionProductCategoriesBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: CategoryItem[] = meta.items || [];

  const updateItem = (idx: number, field: string, val: string | number) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { name: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05D7\u05D3\u05E9\u05D4", count: 0 }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-2"
          style={{ fontWeight: 700 }}
          placeholder="כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm text-center mb-10"
          placeholder="תיאור..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />

        {dropSlot(`secblk-${block.id}-after-subtitle`)}

        {/* Categories Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((item, i) => (
            <div key={i} className="relative group/cat bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-[4/3]">
                <EditableImage
                  src={item.imageUrl}
                  onChangeSrc={(url) => updateItem(i, "imageUrl", url)}
                  isEditMode={isEditMode}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  placeholderClassName="aspect-[4/3]"
                />
              </div>
              <div className="p-3 text-right">
                <InlineEditable
                  value={item.name}
                  onSave={(val) => updateItem(i, "name", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-zap-navy text-sm mb-1"
                  style={{ fontWeight: 600 }}
                  placeholder="שם הקטגוריה..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_name`)}
                />
                {isEditMode ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={item.count}
                      onChange={(e) => updateItem(i, "count", parseInt(e.target.value) || 0)}
                      className="w-12 text-xs text-gray-400 outline-none border-b border-gray-200 focus:border-zap bg-transparent text-center"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-xs text-gray-400">{"מוצרים"}</span>
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs">{item.count} {"מוצרים"}</p>
                )}
              </div>
              {/* Delete */}
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/cat:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-zap-pale rounded-xl flex flex-col items-center justify-center text-zap-light hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[180px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>{"הוספת קטגוריה"}</span>
            </button>
          )}
        </div>

        {dropSlot(`secblk-${block.id}-after-content`)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   3. PROMO BANNER SECTION
   ═══════════════════════════════════════════════ */

const PROMO_BG_STYLES: Record<string, { from: string; to: string }> = {
  "gradient-blue": { from: "#001C32", to: "#052b7a" },
  "gradient-red": { from: "#b91c1c", to: "#ef4444" },
  "gradient-green": { from: "#047857", to: "#10b981" },
  "gradient-purple": { from: "#7e22ce", to: "#a855f7" },
  "gradient-amber": { from: "#b45309", to: "#f59e0b" },
};

const PROMO_COLOR_OPTIONS = [
  { key: "gradient-blue", label: "\u05DB\u05D7\u05D5\u05DC" },
  { key: "gradient-red", label: "\u05D0\u05D3\u05D5\u05DD" },
  { key: "gradient-green", label: "\u05D9\u05E8\u05D5\u05E7" },
  { key: "gradient-purple", label: "\u05E1\u05D2\u05D5\u05DC" },
  { key: "gradient-amber", label: "\u05DB\u05EA\u05D5\u05DD" },
];

function SectionPromoBannerBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const discount = meta.discount || "";
  const buttonLabel = meta.buttonLabel || "";
  const buttonLink = meta.buttonLink || "/catalog";
  const bgColor = meta.bgColor || "gradient-blue";
  const colors = PROMO_BG_STYLES[bgColor] || PROMO_BG_STYLES["gradient-blue"];

  return (
    <div
      className="rounded-2xl p-8 md:p-12 relative overflow-hidden"
      style={{ background: `linear-gradient(to left, ${colors.from}, ${colors.to})` }}
      dir="rtl"
    >
      {/* Decorative circles */}
      <div className="absolute top-[-40px] right-[-40px] w-40 h-40 bg-white/5 rounded-full pointer-events-none" />
      <div className="absolute bottom-[-30px] left-[-30px] w-32 h-32 bg-white/5 rounded-full pointer-events-none" />

      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Discount badge */}
        {(discount || isEditMode) && (
          <div className="mb-4 flex justify-center">
            {isEditMode ? (
              <input
                type="text"
                value={discount}
                onChange={(e) => onUpdateMeta({ discount: e.target.value })}
                className="px-4 py-1.5 text-sm rounded-full bg-white/20 text-white outline-none text-center placeholder-white/50 min-w-[100px]"
                style={{ fontWeight: 700 }}
                dir="rtl"
                placeholder="הנחה..."
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            ) : discount ? (
              <span className="px-4 py-1.5 text-sm rounded-full bg-white/20 text-white" style={{ fontWeight: 700 }}>
                {discount}
              </span>
            ) : null}
          </div>
        )}

        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-4xl text-white mb-3"
          style={{ fontWeight: 800 }}
          placeholder="כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />

        {dropSlot(`secblk-${block.id}-after-title`)}

        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-white/80 text-sm md:text-base mb-6"
          placeholder="תיאור..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />

        {/* CTA Button */}
        {(buttonLabel || isEditMode) && (
          <div className="mb-4">
            {isEditMode ? (
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <input
                  type="text"
                  value={buttonLabel}
                  onChange={(e) => onUpdateMeta({ buttonLabel: e.target.value })}
                  className="px-5 py-2.5 bg-white text-zap-navy rounded-lg text-sm outline-none min-w-[100px] text-center"
                  style={{ fontWeight: 600 }}
                  dir="rtl"
                  placeholder="טקסט כפתור..."
                />
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-white/60">{"קישור:"}</span>
                  <input
                    type="text"
                    value={buttonLink}
                    onChange={(e) => onUpdateMeta({ buttonLink: e.target.value })}
                    className="text-[11px] px-2 py-1 border border-white/20 rounded bg-white/10 text-white w-24"
                    dir="ltr"
                  />
                </div>
              </div>
            ) : buttonLabel ? (
              <span
                role="button"
                tabIndex={0}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-white text-zap-navy rounded-lg text-sm cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {buttonLabel}
                <ArrowLeft className="w-4 h-4" />
              </span>
            ) : null}
          </div>
        )}

        {/* Color selector (edit mode only) */}
        {isEditMode && (
          <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-white/10">
            <span className="text-white/60 text-[11px] ml-2">{"צבע:"}</span>
            {PROMO_COLOR_OPTIONS.map((opt) => {
              const optColors = PROMO_BG_STYLES[opt.key];
              const isSelected = bgColor === opt.key;
              return (
                <span
                  key={opt.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => onUpdateMeta({ bgColor: opt.key })}
                  onKeyDown={(e) => e.key === "Enter" && onUpdateMeta({ bgColor: opt.key })}
                  className={`w-7 h-7 rounded-full cursor-pointer transition-all ${
                    isSelected ? "ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110" : "opacity-70 hover:opacity-100"
                  }`}
                  style={{ background: `linear-gradient(135deg, ${optColors.from}, ${optColors.to})` }}
                  title={opt.label}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   4. STORE BENEFITS SECTION
   ═══════════════════════════════════════════════ */

const BENEFIT_ICONS: Record<string, typeof Truck> = {
  truck: Truck,
  shield: Shield,
  return: RotateCcw,
  lock: Lock,
};

const BENEFIT_ICON_KEYS = Object.keys(BENEFIT_ICONS);

interface BenefitItem {
  icon: string;
  title: string;
  description: string;
}

function SectionStoreBenefitsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: BenefitItem[] = block.meta?.items || [];

  const updateItem = (idx: number, field: string, val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { icon: "truck", title: "\u05D9\u05EA\u05E8\u05D5\u05DF \u05D7\u05D3\u05E9", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05D4\u05D9\u05EA\u05E8\u05D5\u05DF" }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="rounded-2xl p-8 md:p-12" style={{ background: "linear-gradient(135deg, #001C32 0%, #0d2847 100%)" }} dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-10"
          style={{ fontWeight: 700 }}
          placeholder="כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />

        {dropSlot(`secblk-${block.id}-after-title`)}

        {/* Benefits Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {items.map((item, i) => {
            const Icon = BENEFIT_ICONS[item.icon] || Truck;
            return (
              <div key={i} className="relative group/ben text-center">
                {/* Icon */}
                <div className="w-14 h-14 mx-auto mb-3 bg-white/10 rounded-full flex items-center justify-center">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                {/* Title */}
                <InlineEditable
                  value={item.title}
                  onSave={(val) => updateItem(i, "title", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-white text-sm md:text-base mb-1"
                  style={{ fontWeight: 700 }}
                  placeholder="כותרת..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                />
                <InlineEditable
                  value={item.description}
                  onSave={(val) => updateItem(i, "description", val)}
                  isEditMode={isEditMode}
                  tag="p"
                  className="text-blue-200 text-xs md:text-sm"
                  placeholder="תיאור..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_desc`)}
                />

                {/* Icon selector (edit mode) */}
                {isEditMode && (
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {BENEFIT_ICON_KEYS.map((key) => {
                      const BIcon = BENEFIT_ICONS[key];
                      return (
                        <span
                          key={key}
                          role="button"
                          tabIndex={0}
                          onClick={() => updateItem(i, "icon", key)}
                          onKeyDown={(e) => e.key === "Enter" && updateItem(i, "icon", key)}
                          className={`w-6 h-6 rounded flex items-center justify-center cursor-pointer transition-all ${
                            item.icon === key ? "bg-white/30" : "bg-white/10 hover:bg-white/20"
                          }`}
                        >
                          <BIcon className="w-3.5 h-3.5 text-white" />
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Delete */}
                {isEditMode && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => removeItem(i)}
                    onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                    className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/ben:opacity-100 transition-opacity cursor-pointer z-10"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </div>
            );
          })}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-white/30 rounded-2xl flex flex-col items-center justify-center text-white/50 hover:border-white/60 hover:text-white/80 transition-all min-h-[140px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>{"הוספת תכונה"}</span>
            </button>
          )}
        </div>

        {dropSlot(`secblk-${block.id}-after-content`)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   5. FAQ SECTION
   ═══════════════════════════════════════════════ */

interface FAQItem {
  question: string;
  answer: string;
}

function SectionFAQBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: FAQItem[] = meta.items || [];
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const updateItem = (idx: number, field: string, val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { question: "\u05E9\u05D0\u05DC\u05D4 \u05D7\u05D3\u05E9\u05D4?", answer: "\u05EA\u05E9\u05D5\u05D1\u05D4 \u05DC\u05E9\u05D0\u05DC\u05D4 \u05D4\u05D7\u05D3\u05E9\u05D4. \u05DC\u05D7\u05E6\u05D5 \u05DB\u05D3\u05D9 \u05DC\u05E2\u05E8\u05D5\u05DA." }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-3xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-2"
          style={{ fontWeight: 700 }}
          placeholder="כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm text-center mb-10"
          placeholder="תיאור..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />

        {dropSlot(`secblk-${block.id}-after-subtitle`)}

        {/* FAQ Items */}
        <div className="space-y-3">
          {items.map((item, i) => {
            const isOpen = openIndex === i || isEditMode;
            return (
              <div key={i} className="relative group/faq border border-gray-200 rounded-xl overflow-hidden bg-white">
                {/* Question row */}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenIndex(openIndex === i ? null : i);
                    }
                  }}
                  className="flex items-center justify-between w-full px-5 py-4 text-right cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <InlineEditable
                    value={item.question}
                    onSave={(val) => updateItem(i, "question", val)}
                    isEditMode={isEditMode}
                    tag="span"
                    className="text-zap-navy text-sm"
                    style={{ fontWeight: 600 }}
                    placeholder="שאלה..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_q`)}
                  />
                  <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </span>

                {/* Answer */}
                {isOpen && (
                  <div className="px-5 pb-4 border-t border-gray-100 pt-3">
                    <MultilineEditable
                      value={item.answer}
                      onSave={(val) => updateItem(i, "answer", val)}
                      isEditMode={isEditMode}
                      className="text-gray-600 text-sm leading-relaxed"
                      placeholder="תשובה..."
                      {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_a`)}
                    />
                  </div>
                )}

                {/* Delete */}
                {isEditMode && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => removeItem(i)}
                    onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                    className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/faq:opacity-100 transition-opacity cursor-pointer z-10"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Add button */}
        {isEditMode && (
          <button
            onClick={addItem}
            className="w-full mt-4 border-2 border-dashed border-zap-pale rounded-xl py-3 flex items-center justify-center gap-2 text-zap-light hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs" style={{ fontWeight: 500 }}>{"הוספת שאלה"}</span>
          </button>
        )}

        {dropSlot(`secblk-${block.id}-after-content`)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN STORE SECTION RENDERER
   ═══════════════════════════════════════════════ */

export function StoreSectionBlockRenderer({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}): JSX.Element | null {
  const props = { block, isEditMode, onUpdateContent, onUpdateMeta };

  switch (block.type) {
    case "sectionFeaturedProducts":
      return <SectionFeaturedProductsBlock {...props} />;
    case "sectionProductCategories":
      return <SectionProductCategoriesBlock {...props} />;
    case "sectionPromoBanner":
      return <SectionPromoBannerBlock {...props} />;
    case "sectionStoreBenefits":
      return <SectionStoreBenefitsBlock {...props} />;
    case "sectionFAQ":
      return <SectionFAQBlock {...props} />;
    default:
      return null;
  }
}